// GLSL shaders for the animated background.
// Each fragment shader samples its layer texture with a UV offset derived
// from procedural noise — replicating feTurbulence + feDisplacementMap on the GPU.

export const vertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

// Classic Ashima 2D simplex noise (public domain) + fbm helper.
const noiseGLSL = /* glsl */ `
  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }
  float snoise(vec2 v) {
    const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                       -0.577350269189626, 0.024390243902439);
    vec2 i  = floor(v + dot(v, C.yy));
    vec2 x0 = v -   i + dot(i, C.xx);
    vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec4 x12 = x0.xyxy + C.xxzz;
    x12.xy -= i1;
    i = mod289(i);
    vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0))
                            + i.x + vec3(0.0, i1.x, 1.0));
    vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
    m = m * m;
    m = m * m;
    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;
    m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
    vec3 g;
    g.x  = a0.x * x0.x + h.x * x0.y;
    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
    return 130.0 * dot(m, g);
  }
  float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 4; i++) {
      v += a * snoise(p);
      p *= 2.0;
      a *= 0.5;
    }
    return v;
  }
`;

// Grass: low-amplitude vertical undulation (matches grass-sway).
// Stretched to fill the viewport like the other layers — no cover logic so it
// stays fixed on resize instead of shifting around.
export const grassFragmentShader = /* glsl */ `
  precision mediump float;
  uniform sampler2D uTexture;
  uniform float uTime;
  varying vec2 vUv;
  ${noiseGLSL}
  void main() {
    float t = uTime * 0.25;
    float n = fbm(vec2(vUv.x * 5.0 + t, vUv.y * 1.2));
    vec2 offset = vec2(0.0, n * 0.0008);
    vec2 uv = clamp(vUv + offset, vec2(0.0), vec2(1.0));
    gl_FragColor = texture2D(uTexture, uv);
  }
`;

// Water: omnidirectional ripple (matches water-wave) + slow horizontal scroll
// (matches the 30s `water` keyframe parallax in Background.css).
export const waterFragmentShader = /* glsl */ `
  precision mediump float;
  uniform sampler2D uTexture;
  uniform float uTime;
  varying vec2 vUv;
  ${noiseGLSL}
  void main() {
    float t = uTime * 0.10;
    // Lower-frequency noise so ripples are wider and gentler.
    float nx = fbm(vec2(vUv.x * 5.0 + t, vUv.y * 5.0));
    float ny = fbm(vec2(vUv.x * 5.0 + 100.0, vUv.y * 5.0 - t));
    // Match original water-wave scale (~10px / 1920 ≈ 0.005 UV).
    vec2 offset = vec2(nx, ny) * 0.005;
    vec2 uv = vUv + offset;
    uv.x = fract(uv.x + uTime / 50.0);
    gl_FragColor = texture2D(uTexture, uv);
  }
`;

// Day sky: smooth slow drift (matches cloud-wave) + slow horizontal wind scroll.
export const cloudFragmentShader = /* glsl */ `
  precision mediump float;
  uniform sampler2D uTexture;
  uniform float uTime;
  varying vec2 vUv;
  ${noiseGLSL}
  void main() {
    float t = uTime * 0.035;
    float nx = fbm(vec2(vUv.x * 4.0 + t, vUv.y * 4.0));
    float ny = fbm(vec2(vUv.x * 4.0 - t, vUv.y * 4.0 + 30.0));
    vec2 offset = vec2(nx, ny) * 0.014;
    vec2 uv = vUv + offset;
    uv.x = fract(uv.x - uTime / 150.0);
    gl_FragColor = texture2D(uTexture, uv);
  }
`;

// Night sky: layered slow shimmer + fast discrete twinkle (matches star-twinkle)
// + 90s horizontal scroll to match the day variant.
export const starFragmentShader = /* glsl */ `
  precision mediump float;
  uniform sampler2D uTexture;
  uniform float uTime;
  varying vec2 vUv;
  ${noiseGLSL}
  void main() {
    // Slow shimmer: very subtle. Original used scale=4 (~0.002 in UV).
    float slowT = uTime * 0.045;
    float nSlowX = fbm(vec2(vUv.x * 5.0 + slowT, vUv.y * 5.0));
    float nSlowY = fbm(vec2(vUv.x * 5.0, vUv.y * 5.0 + slowT));
    vec2 slowOffset = vec2(nSlowX, nSlowY) * 0.0018;

    // Fast twinkle: discrete jumps every ~2.5s like the original SVG seed animation.
    float step = floor(uTime * 0.4) / 0.4;
    float nFastX = snoise(vec2(vUv * 60.0 + step * 13.0));
    float nFastY = snoise(vec2(vUv * 60.0 - step * 17.0));
    vec2 fastOffset = vec2(nFastX, nFastY) * 0.0008;

    vec2 uv = vUv + slowOffset + fastOffset;
    uv.x = fract(uv.x - uTime / 150.0);
    gl_FragColor = texture2D(uTexture, uv);
  }
`;
